import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useForm } from '../../hooks/useForm';
import { validateEmail } from '../../utils/validations';
import styles from './LoginPage.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCompany } from '../../context/CompanyContext';
import { useSubdomain } from '../../hooks/useSubdomain';
import { getCurrentSubdomain, isSubdomainEnvironment } from '../../utils/subdomainUtils';
import AdsnMarquee from '../../components/AdsMarquee'; // Adjust path to your component
import adsService, { Ads } from '../../services/adsService';
import { BACKEND_BASE_URL } from '../../utils/constants';

interface LoginFormData {
  email: string;
  password: string;
}

const validationRules = {
  email: {
    required: true,
    custom: (value: string) => {
      if (!validateEmail(value)) {
        return 'Please enter a valid email address or Username';
      }
      return null;
    }
  },
  password: {
    required: true,
    minLength: 6
  }
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { name, companyId } = useCompany();
  const { company: subdomainCompany, loading: subdomainLoading } = useSubdomain();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState(name);
  const [subdomain, setSubdomain] = useState('');
  const [isSubdomainEnv, setIsSubdomainEnv] = useState(false);
  const [ads, setAds] = useState<Ads[]>([]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm
  } = useForm<LoginFormData>({
    email: '',
    password: ''
  }, validationRules);

  // Initialize subdomain and company name
  useEffect(() => {
    const currentSubdomain = getCurrentSubdomain();
    const isSubdomain = isSubdomainEnvironment();

    setSubdomain(currentSubdomain);
    setIsSubdomainEnv(isSubdomain);

    if (subdomainCompany) {
      setCompanyName(subdomainCompany.name);
    } else if (isSubdomain && currentSubdomain) {
      setCompanyName('Invoice & Cloud Management');
    } else {
      setCompanyName(name);
    }
  }, [subdomainCompany, name]);

  // Fetch ads (replace with your real API)
  useEffect(() => {
    async function fetchAds() {
      try {
        const res = await adsService.getAll();
        setAds(res || []);
      } catch (err) {
        console.error('Failed to load ads:', err);
      }
    }
    fetchAds();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const user = await login({
        email: values.email,
        password: values.password,
        company_id: companyId
      });

      toast.success('Login successful!');

      if ((user?.role === 'admin' || user?.role === 'super_admin') && user?.istotpenabled) {
        navigate('/verify-tfa');
        return;
      }

      if (user?.role === 'admin' && user?.is_cloud_active) {
        navigate('/employees', { replace: true });
        return;
      }

      if (user?.is_cloud_active) {
        navigate('/cloud-storage', { replace: true });
        return;
      }

      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error('Invalid email or password');
      console.error('Login failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof LoginFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field)(e.target.value);
  };

  const handleInputBlur = (field: keyof LoginFormData) => () => {
    handleBlur(field)();
  };

  return (
    <div className={styles.loginWrapper}>
      {/* Left - Login Form */}
      <div className={styles.loginLeft}>
        <div className={styles.loginCard}>
          <div className={styles.header}>
            {subdomainLoading ? <h1>Loading...</h1> : <h1>{companyName}</h1>}
            <p>Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.label}>
                Email or Username
              </label>
              <input
                id="email"
                name="login"
                value={values.email}
                onChange={handleInputChange('email')}
                onBlur={handleInputBlur('email')}
                className={`${styles.input} ${errors.email && touched.email ? styles.error : ''}`}
                placeholder="Enter your email or username"
                disabled={isSubmitting}
              />
              {errors.email && touched.email && (
                <span className={styles.errorText}>{errors.email}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={values.password}
                onChange={handleInputChange('password')}
                onBlur={handleInputBlur('password')}
                className={`${styles.input} ${errors.password && touched.password ? styles.error : ''}`}
                placeholder="Enter your password"
                disabled={isSubmitting}
              />
              {errors.password && touched.password && (
                <span className={styles.errorText}>{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className={styles.poweredBy}>
            <p>Powered by Sentech Technologies</p>
          </div>
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>

      {ads.length > 0 && (
      <div className={styles.loginRight}>
        <AdsnMarquee instructions={ads} />
      </div>
      )};
    </div>
  );
};

export default LoginPage;
